import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fromInteger, getInputPrecision } from '@repo/money'
import type { Bet } from 'src/types/domain/Bet'
import type { MarbleGame } from 'src/types/domain/Game'
import type { AuthenticatedPlayer } from 'src/types/domain/Player'
import type { KnownGameConfig } from 'src/types/domain/GameConfig'
import { getLatestEvent } from 'src/utils/raceUtils'
import { useCrashBetSubmit } from 'src/hooks/useCrashBetSubmit'
import { useCashout } from 'src/hooks/useCashout'
import { useCrashMultiplier } from 'src/hooks/useCrashMultiplier'
import { useBetLock, type BetLockSent } from 'src/hooks/useBetLock'
import { useStageTwo, type StageTwo } from 'src/hooks/useStageTwo'
import { stageTwoAt } from 'src/utils/betLock'
import { useBeaconClimbProven } from './useFrameBeacon'
import { useGetCurrentBets } from 'src/hooks/useGetCurrentBets'
import { usePlayerBalance } from 'src/hooks/usePlayerBalance'
import { usePlayerBets } from 'src/hooks/usePlayerBets'
import { useGetGameBetConfig } from 'src/store/game'
import { useBetActions, useBetStore } from 'src/store/bet'
import { usePlayerActions, useIsDemoMode } from 'src/store/player'
import { useDemoStore } from 'src/store/demo'
import { useUIStore } from 'src/store/ui'
import { emitBalanceInsufficient } from 'src/services/postMessage/emitters'
import {
  clampChipToZone,
  classifyChipPlacement,
  doubleStakes,
  emptyStakes,
  generateCrashChips,
  resolveSubmitAdjustments,
  resolveZoneRiding,
  totalStaked as sumStakes,
  type StakeLimits,
  type SubmittedCashoutTerms,
  type Zone,
  type ZoneStakes
} from './crashChipLogic'
import { resolveCrashEventInfo } from './crashEventInfo'
import { resolveRehydratedZoneBets } from './crashRehydrate'
import { useVisualCrashStatus } from './useVisualCrash'

// Statuses that mean the bet is no longer active (settled one way or another).
const RESOLVED_STATUSES = ['won', 'lost', 'cashed_out', 'cancelled']

// Statuses that mean the player BANKED the zone this round (v12 — these
// free the zone into the next-round builder; see resolveZoneCardState).
const CASHED_STATUSES = ['won', 'cashed_out']

// Default per-zone auto-cash-out multiplier, matching useCrashAutoBet.
const DEFAULT_AUTO_CASHOUT_MULTIPLIER = 2.0

// Submit ~1.5s into the closing phase (mirrors ChipPlacementPanel). Leaves
// buffer before the round actually starts (and closes server-side).
const SUBMIT_DELAY_MS = 1500

// v12 tap shield (R15): the moment a zone's bet flips to cashed the card
// converts from the tappable CASH-OUT button into the tappable BUILDER —
// same spot, opposite meaning. A tap inside this window is the cash-out
// reflex still landing, not a bet: the freed zone ignores placeChip for
// this long after the flip (and while a cash-out POST is in flight).
export const CASHOUT_CONVERT_SHIELD_MS = 400

// Optimistic cash-out: how long the 'cashing' settling card may wait for a
// server answer before degrading back to the live card. The POST itself has
// no timeout, so a hung fetch would otherwise pin the overlay for the whole
// round. Generous vs the ~500ms round-trip: only a genuinely dead request
// hits it, and if that request later succeeds anyway the realtime push /
// store flip still lands the real banner.
export const OPTIMISTIC_CASHOUT_TIMEOUT_MS = 7000

// After-crash rejection: the "just missed" notice is DEFERRED until the
// crash is visible on this viewer's frames (firing early would announce the
// crash ~1.5s before the video shows it — the exact tell class the visual
// hold exists to kill). This fallback fires it anyway if the visual crash
// never lands (dropped crash event / undriven edge cases); it sits above
// useVisualCrash's own 5s stale-beacon grace.
export const JUST_MISSED_FALLBACK_MS = 6000

export interface ZoneAuto {
  autoEnabled: boolean
  cashoutEnabled: boolean
  cashoutMultiplier: number
}

// ── Player-facing notices (v12.4 ask 7) ──────────────────────────────────
// The hook fires ABSTRACT notice ids; the panel maps them to literal t()
// copy (useCrashNotice.ts) so this hook stays i18n-free and unit-testable.
// One transient slot, replace-not-stack — `seq` bumps on every fire so the
// panel re-keys (and re-animates) duplicates, mirroring the reference
// chip-panel notice contract (G13).
export type CrashNoticeId =
  | 'cashout-locked-round'
  | 'cashout-off-next-round'
  | 'cashout-on-next-round'
  | 'max-reached'
  | 'insufficient-balance'
  | 'balance-initializing'
  | 'insufficient-balance-auto-stopped'
  | 'insufficient-balance-not-placed'
  | 'below-minimum-not-placed'
  | 'stake-reduced'
  | 'auto-stopped-edit'
  | 'submit-aborted'
  | 'bet-failed'
  | 'cashout-failed'
  | 'cashout-just-missed'
  | 'session-expired'

export type CrashNoticeTone = 'warning' | 'success' | 'info'

export interface CrashNotice {
  id: CrashNoticeId
  tone: CrashNoticeTone
  seq: number
}

// What a zone tap did — the zone component keys its feedback off this:
//   placed  → flying chip + chip sound.
//   refused → cancel sound (the hook fired the reasoned notice).
//   inert   → nothing (tap shield / in-flight guard — a reflex tap, not an
//             action; it must neither animate nor scold).
export type PlaceChipOutcome = 'placed' | 'refused' | 'inert'

// The optimistic cash-out overlay for one zone — the 'cashing' settling
// card's figures, snapshotted at the tap from the DISPLAY clock (what the
// player saw). Held HERE, never in the store: the reward chime + win banner
// both derive from the store's terminal status, so no celebration can fire
// before the server confirms. The server settles on its own (leading) clock,
// so the confirmed figure is ≥ this one — except when the operator liability
// ceiling clamps a very large win, which is why the confirmed banner always
// shows the SERVER figure, never this snapshot.
export interface OptimisticCashout {
  betId: string
  multiplier: number
  payout: number
}

export interface UseCrashChipBetting {
  chips: number[]
  selectedChipIdx: number
  selectChip: (idx: number) => void
  stakes: ZoneStakes
  placeChip: (zone: Zone) => PlaceChipOutcome
  /** Doubles buildable stakes. Reports whether anything actually changed so
   *  the caller can voice the tap honestly (v49). */
  double: () => 'changed' | 'inert'
  undo: () => void
  clear: () => 'changed' | 'inert'
  // Clear a single zone's (queued) stake — the ✕ on the next-round builder.
  // Counts as a manual edit, so it also disables that zone's auto-rebet.
  clearZone: (zone: Zone) => void
  repeat: () => void
  canUndo: boolean
  canControl: boolean
  // TRUE once a real (non-zero) stake snapshot exists to repeat — gates the
  // strip's Repeat affordance so it never renders enabled-but-inert (v47).
  hasRepeatable: boolean
  zoneAuto: Record<Zone, ZoneAuto>
  setZoneAuto: (zone: Zone, patch: Partial<ZoneAuto>) => void
  myBet: Record<Zone, Bet | undefined>
  // Cash out a zone. `shownMultiplier` is the display-clock value the zone
  // is rendering at the tap — the optimistic overlay snapshots it so the
  // settling card shows exactly what the player saw (event clock fallback).
  cashout: (zone: Zone, shownMultiplier?: number) => Promise<void>
  cashingOut: Record<Zone, boolean>
  // Per-zone optimistic cash-out overlay (null = none). Drives the 'cashing'
  // settling card; see OptimisticCashout.
  optCashout: Record<Zone, OptimisticCashout | null>
  liveMultiplier: number
  isClimbing: boolean
  hasStartedClimbing: boolean
  isBettingOpen: boolean
  // The round crashed (a `race_crashed` / `race_ended` event has landed). Drives
  // the widget-side crash payoff (bars → Busted / HUD crash stamp) in sync with
  // the video's baked hit — independent of per-bet settlement, which can lag.
  hasCrashed: boolean
  // The multiplier the round crashed at (from the crash event's payload), for
  // the HUD "× Crashed @ 2.53x" stamp and the busted bars. Undefined until the
  // round crashes.
  crashedMultiplier: number | undefined
  totalStaked: number
  balance: number
  currency: string
  // The currency's wire/input decimal precision — the same precision every
  // stake rounding (clampChipToZone, totalStaked) uses, exposed so the panel
  // can round its own bet-amount sums (in-play) identically.
  inputPrecision: number
  minBet: number
  maxBet: number
  // Per-zone amounts snapshotted the moment this round's submission fires
  // (presentation-only: drives the locked/submitted stake cards so the zones
  // never go empty between submit and bet-accepted). Zones whose submit
  // failed (no bet id) are zeroed once the POSTs settle; reset to zero on
  // the race_ready round reset.
  submittedStakes: ZoneStakes
  // Per-zone cash-out terms snapshotted the moment this round's submission
  // fires — the terms ACTUALLY SUBMITTED with each bet (v10). Client-owned
  // truth for the locked pill: the server-normalized bet:accepted payload
  // replaces the optimistic bet WITHOUT autoCashoutMultiplier (BetDTO never
  // carries it), so the bet in the store cannot be trusted to state the
  // riding terms. Same lifecycle as submittedStakes: cleared for zones whose
  // submit failed, reset on the race_ready round reset.
  submittedCashout: Record<Zone, SubmittedCashoutTerms | undefined>
  // Stage 2 of a hard round (DESIGN §6): "placing" from the lock while a zone's
  // bet is unanswered, "confirming" from the board's estimate of K. Null on
  // every other round.
  stageTwo: StageTwo
  // The transient player-facing notice (v12.4 ask 7) — abstract id + tone,
  // rendered by the panel's notice pill. Replace-not-stack; seq re-keys
  // duplicates.
  notice: CrashNotice | null
  /** Fire a panel notice imperatively (v34: the locked-instrument tap). */
  fireNotice: (id: CrashNoticeId, tone?: CrashNoticeTone) => void
  // Whether each zone's bet is still awaiting server acceptance (in the bet
  // store's unacceptedBets). Gates the busted card: a bet the server never
  // accepted must not render as a loss (G10).
  myBetUnaccepted: Record<Zone, boolean>
}

const emptyZoneAuto = (): ZoneAuto => ({
  autoEnabled: false,
  cashoutEnabled: false,
  cashoutMultiplier: DEFAULT_AUTO_CASHOUT_MULTIPLIER
})

export function useCrashChipBetting(
  game: MarbleGame,
  player: AuthenticatedPlayer,
  _betConfig: KnownGameConfig['bet']
): UseCrashChipBetting {
  // Read the bet config from the store (the passed-in one is the same object;
  // reading here keeps parity with the other panels and re-renders on change).
  const betConfig = useGetGameBetConfig(player) ?? _betConfig
  const { liveMultiplier, isClimbing } = useCrashMultiplier(game)
  const { currentBets } = useGetCurrentBets()
  const { cashout: cashoutBet } = useCashout()
  const { updateBalance } = usePlayerBalance()
  const { fetchBets } = usePlayerBets()
  const betActions = useBetActions()
  const playerActions = usePlayerActions()
  const isDemoMode = useIsDemoMode()

  // One submit hook per zone (left → slot0, right → slot1 nonce).
  const { submitBet: submitBetLeft } = useCrashBetSubmit({ game, player, slotIndex: 0 })
  const { submitBet: submitBetRight } = useCrashBetSubmit({ game, player, slotIndex: 1 })

  const currency = player.balance.currency

  const inputPrecision = useMemo(() => {
    try {
      return getInputPrecision(currency)
    } catch {
      return 2
    }
  }, [currency])

  const minBet = useMemo(
    () => fromInteger(betConfig.limits.PickWinner?.min ?? betConfig.minBetAmount, currency),
    [betConfig, currency]
  )
  const maxBet = useMemo(
    () => fromInteger(betConfig.limits.PickWinner?.max ?? betConfig.maxBetAmount, currency),
    [betConfig, currency]
  )

  const balance = player.balance.amount

  // Chip strip — parity with ChipPlacementPanel (B7/B8/R2):
  //  B8: a GROWING balance high-watermark (grows on top-ups / big wins, never
  //      shrinks — a single losing round must not strip the larger chips off
  //      the strip). Updated inline each render like the reference.
  //  B7: operator custom-multiplier chips when the config ships them.
  //  R2: the exact min bet prepended when the smallest clean chip is above it.
  const balanceHighWatermarkRef = useRef(balance)
  if (balance > balanceHighWatermarkRef.current) balanceHighWatermarkRef.current = balance
  const chipMultipliers = betConfig.chipMultipliers
  const chips = useMemo(
    () =>
      generateCrashChips({
        minBet,
        maxBet,
        balance: balanceHighWatermarkRef.current,
        chipMultipliers: chipMultipliers ?? [],
        targetCount: 8
      }),
    [minBet, maxBet, chipMultipliers, balance]
  )

  // Default the selection to a mid chip, clamped to the available range.
  const [selectedChipIdx, setSelectedChipIdx] = useState(() =>
    Math.min(2, Math.max(0, chips.length - 1))
  )
  const selectChip = useCallback(
    (idx: number) => setSelectedChipIdx(Math.min(Math.max(0, idx), chips.length - 1)),
    [chips.length]
  )

  const [stakes, setStakes] = useState<ZoneStakes>(emptyStakes)
  const stakesRef = useRef(stakes)
  stakesRef.current = stakes

  // Locked/submitted snapshot for the current round (see the interface note).
  const [submittedStakes, setSubmittedStakes] = useState<ZoneStakes>(emptyStakes)
  const submittedStakesRef = useRef(submittedStakes)
  submittedStakesRef.current = submittedStakes

  // The zone bets as currently selected through myBetIdRef (assigned after
  // the myBet memo below) — lets the strip-op callbacks read the riding
  // facts at call time without re-creating on every store churn.
  const myBetRef = useRef<Record<Zone, Bet | undefined>>({ left: undefined, right: undefined })

  // v12 tap shield (R15): per-zone timestamp of the bet's flip into a
  // cashed status. placeChip is inert on that zone until
  // CASHOUT_CONVERT_SHIELD_MS have passed. Cleared ONLY by the race_ready
  // round reset (R20).
  const cashShieldRef = useRef<Record<Zone, number | undefined>>({
    left: undefined,
    right: undefined
  })

  // The cash-out terms actually submitted with each zone's bet (v10 — see
  // the interface note). Snapshotted alongside submittedStakes.
  const [submittedCashout, setSubmittedCashout] = useState<
    Record<Zone, SubmittedCashoutTerms | undefined>
  >({ left: undefined, right: undefined })

  const [zoneAuto, setZoneAutoState] = useState<Record<Zone, ZoneAuto>>({
    left: emptyZoneAuto(),
    right: emptyZoneAuto()
  })
  const zoneAutoRef = useRef(zoneAuto)
  zoneAutoRef.current = zoneAuto

  const undoHistoryRef = useRef<ZoneStakes[]>([])
  const [undoDepth, setUndoDepth] = useState(0)
  const lastStakesRef = useRef<ZoneStakes>(emptyStakes())
  // TRUE once lastStakesRef holds a real (non-zero) snapshot — the strip's
  // Repeat affordance renders only when a tap can actually do something
  // (v47: a fresh session showed an ENABLED Repeat whose taps did nothing).
  const [hasRepeatable, setHasRepeatable] = useState(false)

  // Per-zone cash-out in-flight guard (FIX 4): blocks a double-tap from firing
  // a second POST against a still-pending bet.
  const [cashingOut, setCashingOut] = useState<Record<Zone, boolean>>({ left: false, right: false })
  const cashingOutRef = useRef(cashingOut)
  cashingOutRef.current = cashingOut

  // ── Optimistic cash-out overlay (Task A) ───────────────────────────────
  // The 'cashing' settling card, per zone — mounted the instant a tap passes
  // the arming gate, resolved by the server response (or the timeout). The
  // store bet is NEVER mutated optimistically.
  const [optCashout, setOptCashout] = useState<Record<Zone, OptimisticCashout | null>>({
    left: null,
    right: null
  })

  // The visual-crash status gates the after-crash "just missed" notice so a
  // rejection can never announce the crash before this viewer's frames show
  // it. Ref-mirrored so cashout() stays identity-stable.
  const visualCrashStatus = useVisualCrashStatus()
  const visualCrashStatusRef = useRef(visualCrashStatus)
  visualCrashStatusRef.current = visualCrashStatus
  const pendingJustMissedRef = useRef(false)
  const justMissedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const limits: StakeLimits = useMemo(
    () => ({ minBet, maxBet, balance, precision: inputPrecision }),
    [minBet, maxBet, balance, inputPrecision]
  )
  // Live limits for submit-time re-validation (triggerSubmission reads this so
  // it always sees the current balance/min/max without being re-created).
  const limitsRef = useRef(limits)
  limitsRef.current = limits

  // Live balance-loaded fact for the placement guard (G3) — ref so placeChip
  // stays identity-stable.
  const balanceLoadedRef = useRef(player.balanceLoaded)
  balanceLoadedRef.current = player.balanceLoaded

  // ── Player-facing notice slot (v12.4 ask 7) ────────────────────────────
  // Replace-not-stack; seq re-keys duplicates so the panel re-fires the
  // entrance animation. No dwell timer here — the display layer owns it.
  const noticeSeqRef = useRef(0)
  const [notice, setNotice] = useState<CrashNotice | null>(null)
  const fireNotice = useCallback((id: CrashNoticeId, tone: CrashNoticeTone = 'warning') => {
    noticeSeqRef.current += 1
    setNotice({ id, tone, seq: noticeSeqRef.current })
  }, [])

  const raceEvents = game.races[0].raceEvents
  const latestEvent = getLatestEvent(raceEvents)
  const phase = latestEvent?.eventType

  // The hard close (rev 4, DESIGN §6): on a round whose race_ready says
  // bet_close "hard", both zones lock at the first of race_starting's arrival
  // and the board's own countdown, and are sent at once from `onHardLock`
  // (declared with the submission below; read through a ref, so it may be
  // defined after this call). Every other round: `hard` false, today's board.
  const onHardLockRef = useRef<(sent: BetLockSent) => void>(() => undefined)
  const betLock = useBetLock({
    race: game.races[0],
    previousRace: game.races[1],
    onLock: (sent) => onHardLockRef.current(sent)
  })
  // Locked: the zones are this round's no more, and a chip laid now is for the next round.
  const hardLocked = betLock.hard && betLock.lockedBy !== undefined
  const isBettingOpen = (phase === 'race_ready' || phase === 'race_starting') && !hardLocked
  // The bets this round's hard lock put on the wire, for stage 2's line.
  const [hardSentBetIds, setHardSentBetIds] = useState<string[]>([])
  const stageTwo = useStageTwo({
    lockedAt: betLock.lockedAt,
    lockLeadS: betLock.lockLeadS,
    betIds: hardSentBetIds
  })

  // Cash-out only unlocks once the multiplier actually starts climbing (the
  // reveal-delay flat window at 1.00x has passed).
  //
  // Change 3 (2026-08-17): the event-clock term (liveMultiplier, wall-clock
  // derived) under-reads on a slow client clock and armed the button late by
  // the skew. A frame showing race time t proves the engine reached t, so the
  // beacon latch (two consecutive admits ≥ reveal — useFrameBeacon) is an
  // equally valid, skew-immune arm. INSIDE the isClimbing conjunction on
  // purpose: race_crashed / race_ended / race_cancelled flip isClimbing false
  // and close BOTH arms at once (instant-crash tails and voided rounds can
  // never beacon-arm), and beacon-arming implies race_started was received.
  // This predicate is THE arming ceiling — the render gate (CrashBetZone via
  // isCashoutArmed) and the tap gate (cashout() below) both read it, so the
  // two can never disagree (a render-only change ships a button whose taps
  // silently no-op).
  const beaconClimbProven = useBeaconClimbProven()
  const hasStartedClimbing = isClimbing && (liveMultiplier > 1 || beaconClimbProven)

  // Crash detection (additive): a `race_crashed` (or `race_ended`) event carries
  // the final multiplier. ONE shared extraction (resolveCrashEventInfo — pure,
  // tested) scans ALL crash events for the first usable multiplier, so a
  // payload-less race_crashed can never mask race_ended's settled M.
  const { crashKnown: hasCrashed, crashedMultiplier } = resolveCrashEventInfo(raceEvents)

  const totalStaked = sumStakes(stakes, inputPrecision)

  // ---------------------------------------------------------------------------
  // Chip placement + strip controls
  // ---------------------------------------------------------------------------
  // Commit a new stakes value: push the previous value onto the undo stack and
  // update state — all outside a `setStakes` updater so StrictMode's double
  // invocation can't duplicate history (FIX 7). `stakesRef.current` is the
  // source of truth for the previous value.
  const commitStakes = useCallback((next: ZoneStakes) => {
    const prev = stakesRef.current
    if (next.left === prev.left && next.right === prev.right) return
    undoHistoryRef.current.push(prev)
    setUndoDepth(undoHistoryRef.current.length)
    setStakes(next)
  }, [])

  // NOTE (next-round betting): chip placement is deliberately NOT gated on the
  // betting window. During a live round, placed chips accumulate as QUEUED
  // next-round stakes — the submission pipeline below stays single-shot per
  // round (guarded by submissionStartedRef + observedBettingOpenRef), so queued
  // stakes only ever leave the widget at the next round start, re-validated by
  // resolveSubmittableStakes. Placement clamps (balance/min/max) apply as ever.
  // v12 (R10): whether a zone's stake may be edited by the STRIP ops
  // (double / repeat / global clear). A riding zone's kept auto stake is
  // INVISIBLE (its card shows the live ride) — strip ops must not silently
  // mutate it. Reads refs so the callbacks stay identity-stable.
  const zoneBuildable = useCallback(
    (zone: Zone) => !resolveZoneRiding(myBetRef.current[zone], submittedStakesRef.current[zone]),
    []
  )

  const placeChip = useCallback(
    (zone: Zone): PlaceChipOutcome => {
      // v12 tap shield (R15): a tap right after the cashed flip is the
      // cash-out reflex landing on the freed builder — inert. Also inert
      // while this zone's cash-out POST is still in flight.
      if (cashingOutRef.current[zone]) return 'inert'
      const shieldAt = cashShieldRef.current[zone]
      if (shieldAt !== undefined && Date.now() - shieldAt < CASHOUT_CONVERT_SHIELD_MS)
        return 'inert'
      const chip = chips[selectedChipIdx]
      if (chip === undefined) return 'inert'
      // G3: never stake before the real balance has loaded (non-blocking
      // launch renders the game before the operator wallet answers).
      if (!balanceLoadedRef.current) {
        fireNotice('balance-initializing')
        return 'refused'
      }
      // G1/G2: a refused tap carries its reason (pure + tested); the zone
      // cap is checked first so the specific message wins (reference rule).
      const verdict = classifyChipPlacement(stakesRef.current, zone, chip, limitsRef.current)
      if (verdict.kind === 'max-reached') {
        fireNotice('max-reached')
        return 'refused'
      }
      if (verdict.kind === 'insufficient-balance') {
        fireNotice('insufficient-balance')
        emitBalanceInsufficient({
          requiredAmount: sumStakes(stakesRef.current, limitsRef.current.precision) + chip,
          currentBalance: limitsRef.current.balance,
          currency
        })
        return 'refused'
      }
      commitStakes(verdict.next)
      // Manual edit stops that zone's auto-rebet (matches chip-placement) —
      // and says so (G12).
      if (zoneAutoRef.current[zone].autoEnabled) {
        setZoneAutoState((prev) => ({ ...prev, [zone]: { ...prev[zone], autoEnabled: false } }))
        fireNotice('auto-stopped-edit')
      }
      return 'placed'
    },
    [chips, selectedChipIdx, currency, commitStakes, fireNotice]
  )

  // v49: double/clear REPORT their outcome ('changed' | 'inert') so the
  // panel can voice them honestly — the ×2 punch + chip sound only on a
  // genuine change, never on a masked no-op (a riding auto-zone's kept
  // stake used to punch gold while nothing anywhere moved).
  const double = useCallback((): 'changed' | 'inert' => {
    const prev = stakesRef.current
    const doubled = doubleStakes(prev, limits)
    // R10 mask: only buildable zones double — a riding zone's invisible
    // kept stake stays exactly what the player last saw.
    const next = {
      left: zoneBuildable('left') ? doubled.left : prev.left,
      right: zoneBuildable('right') ? doubled.right : prev.right
    }
    // Nothing grew on any BUILDABLE staked zone → say why (reference:
    // "Insufficient balance to fully double" / "Max reached on all options").
    const anyBuildableStake =
      (zoneBuildable('left') && prev.left > 0) || (zoneBuildable('right') && prev.right > 0)
    if (anyBuildableStake && next.left === prev.left && next.right === prev.right) {
      const balanceRoom = limitsRef.current.balance - (prev.left + prev.right)
      if (balanceRoom <= 0) {
        fireNotice('insufficient-balance')
        emitBalanceInsufficient({
          requiredAmount: (prev.left + prev.right) * 2,
          currentBalance: limitsRef.current.balance,
          currency
        })
      } else {
        fireNotice('max-reached')
      }
      return 'inert'
    }
    if (next.left === prev.left && next.right === prev.right) return 'inert'
    commitStakes(next)
    return 'changed'
  }, [limits, commitStakes, zoneBuildable, currency, fireNotice])

  const undo = useCallback(() => {
    const prev = undoHistoryRef.current.pop()
    setUndoDepth(undoHistoryRef.current.length)
    if (prev) setStakes(prev)
  }, [])

  const clear = useCallback((): 'changed' | 'inert' => {
    const prev = stakesRef.current
    if (prev.left === 0 && prev.right === 0) return 'inert'
    // R10 mask: global clear zeroes only buildable zones — never a riding
    // zone's invisible kept stake.
    const next = {
      left: zoneBuildable('left') ? 0 : prev.left,
      right: zoneBuildable('right') ? 0 : prev.right
    }
    if (next.left === prev.left && next.right === prev.right) return 'inert'
    lastStakesRef.current = prev
    setHasRepeatable(true)
    commitStakes(next)
    return 'changed'
  }, [commitStakes, zoneBuildable])

  const clearZone = useCallback(
    (zone: Zone) => {
      // v12.1 minor (a): the SAME cash-out-reflex shield as placeChip. When a
      // zone cashes with auto ON, the card converts in place and the queued
      // ✕ lands inside the old CASH OUT button's footprint — a late reflex
      // tap must not wipe the auto-rebet stake (and disable auto). Also inert
      // while this zone's cash-out POST is in flight.
      if (cashingOutRef.current[zone]) return
      const shieldAt = cashShieldRef.current[zone]
      if (shieldAt !== undefined && Date.now() - shieldAt < CASHOUT_CONVERT_SHIELD_MS) return
      const prev = stakesRef.current
      if (prev[zone] === 0) return
      commitStakes({ ...prev, [zone]: 0 })
      // Clearing is a manual edit — stop that zone's auto-rebet too, otherwise
      // the auto would silently re-stake the amount the player just removed.
      // And say so (G12).
      if (zoneAutoRef.current[zone].autoEnabled) {
        setZoneAutoState((p) => ({ ...p, [zone]: { ...p[zone], autoEnabled: false } }))
        fireNotice('auto-stopped-edit')
      }
    },
    [commitStakes, fireNotice]
  )

  const repeat = useCallback(() => {
    const last = lastStakesRef.current
    if (last.left === 0 && last.right === 0) return
    // Re-clamp the restored stakes to the current limits (balance may have
    // shifted since last round).
    let restored = emptyStakes()
    restored = clampChipToZone(restored, 'left', last.left, limits)
    restored = clampChipToZone(restored, 'right', last.right, limits)
    // R10 mask: restore only onto buildable zones — a riding zone must not
    // gain an invisible queued stake.
    const prev = stakesRef.current
    commitStakes({
      left: zoneBuildable('left') ? restored.left : prev.left,
      right: zoneBuildable('right') ? restored.right : prev.right
    })
  }, [limits, commitStakes, zoneBuildable])

  const canUndo = undoDepth > 0
  // Strip controls follow the stakes, not the phase — during a live round
  // they operate on the queued next-round stakes. v49: BUILDABLE stakes
  // only — a lone riding auto-zone's kept stake used to light Clear/×2
  // whose taps were R10-masked into silent no-ops (every auto-rebet
  // player's first live-round adjustment attempt).
  const canControl =
    (zoneBuildable('left') ? stakes.left : 0) + (zoneBuildable('right') ? stakes.right : 0) > 0

  const setZoneAuto = useCallback((zone: Zone, patch: Partial<ZoneAuto>) => {
    setZoneAutoState((prev) => ({ ...prev, [zone]: { ...prev[zone], ...patch } }))
  }, [])

  // ---------------------------------------------------------------------------
  // Submit at round start (single-shot per round)
  // ---------------------------------------------------------------------------
  const submissionStartedRef = useRef(false)
  const observedBettingOpenRef = useRef(false)
  const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // A hard round's send (hard close rev 4): set by `onHardLock` just before it
  // triggers the submission, read and cleared by that one call.
  const hardSendRef = useRef<{ joinDeadline: number } | null>(null)
  const myBetIdRef = useRef<Record<Zone, string | undefined>>({ left: undefined, right: undefined })
  const [, forceBetIds] = useState(0)

  const triggerSubmission = useCallback(async () => {
    // A hard round's lock hands its send in here (`onHardLock`), for this call only.
    const hardSend = hardSendRef.current
    hardSendRef.current = null
    if (submissionStartedRef.current) return
    // Never submit before the real balance has loaded (defense-in-depth).
    if (!player.balanceLoaded) return
    const current = stakesRef.current
    if (current.left <= 0 && current.right <= 0) return
    submissionStartedRef.current = true

    // Submit-time re-validation against the LIVE limits (FIX 2 + FIX 3):
    // balance may have dropped since placement (esp. with auto-rebet), a
    // partial clamp may have landed below minBet, or a stake may exceed maxBet.
    // Only zones with a safe, at-or-above-minimum amount are submitted, and we
    // submit that resolved amount — not the raw stake.
    const adjustments = resolveSubmitAdjustments(current, limitsRef.current)
    const resolved = adjustments.resolved

    // v12.4 ask 7 (G4/G5): trims/drops are no longer silent. A zone the
    // balance can no longer cover is CLEARED (it would otherwise silently
    // re-attempt every round forever) and its auto-rebet stops — with the
    // reference notices; a below-min stake stays queued (the R12 clearable
    // rule) but the player is told why nothing went out. All money figures
    // here are placed stakes / the live balance — never derived winnings.
    for (const zone of ['left', 'right'] as Zone[]) {
      const adj = adjustments[zone]
      if (adj.kind === 'ok') continue
      if (adj.kind === 'dropped') {
        if (adj.reason === 'balance') {
          setStakes((prev) => (prev[zone] === 0 ? prev : { ...prev, [zone]: 0 }))
          const autoWasOn = zoneAutoRef.current[zone].autoEnabled
          if (autoWasOn) {
            setZoneAutoState((prev) => ({
              ...prev,
              [zone]: { ...prev[zone], autoEnabled: false }
            }))
          }
          fireNotice(
            autoWasOn ? 'insufficient-balance-auto-stopped' : 'insufficient-balance-not-placed'
          )
          emitBalanceInsufficient({
            requiredAmount: current[zone],
            currentBalance: limitsRef.current.balance,
            currency
          })
        } else {
          fireNotice('below-minimum-not-placed')
        }
      } else {
        // trimmed — a reduced amount still goes out.
        fireNotice('stake-reduced')
        if (adj.reason === 'balance') {
          emitBalanceInsufficient({
            requiredAmount: current[zone],
            currentBalance: limitsRef.current.balance,
            currency
          })
        }
      }
    }

    // v12 (R11): the submission is the undo boundary. Undo must never
    // resurrect pre-submit stakes into the post-submit world (it would
    // conjure a queued stake the player already saw leave the builder).
    undoHistoryRef.current = []
    setUndoDepth(0)

    // Lock the zones visually BEFORE the POSTs go out — the staked cards
    // stay rendered (placing → confirmed) instead of emptying while the
    // submits are in flight.
    setSubmittedStakes({ left: resolved.left, right: resolved.right })

    // Remember last round's actual submitted bets so Repeat restores them
    // (FIX 6). Previously only Clear set this. v47: only when something
    // actually went out — an all-dropped submit (both stakes zeroed by the
    // balance clamp) must not clobber the previous repeatable snapshot
    // with zeros and kill Repeat for the session.
    if (resolved.left + resolved.right > 0) {
      lastStakesRef.current = { left: resolved.left, right: resolved.right }
      setHasRepeatable(true)
    }

    const auto = zoneAutoRef.current

    // Snapshot the cash-out terms going out on the wire (v10): the locked
    // pill states THESE — the bet:accepted replacement strips
    // autoCashoutMultiplier off the store bet, so only this client-owned
    // snapshot provably survives the round.
    setSubmittedCashout({
      left:
        resolved.left > 0
          ? { enabled: auto.left.cashoutEnabled, multiplier: auto.left.cashoutMultiplier }
          : undefined,
      right:
        resolved.right > 0
          ? { enabled: auto.right.cashoutEnabled, multiplier: auto.right.cashoutMultiplier }
          : undefined
    })

    let changed = false
    let anyAborted = false

    if (hardSend) {
      // A hard round sends both zones AT ONCE (each waits for the same join, to
      // the same deadline), never one after the other.
      const options = { joinDeadline: hardSend.joinDeadline }
      const [left, right] = await Promise.all([
        resolved.left > 0
          ? submitBetLeft(
              String(resolved.left),
              auto.left.cashoutEnabled ? auto.left.cashoutMultiplier : undefined,
              options
            )
          : undefined,
        resolved.right > 0
          ? submitBetRight(
              String(resolved.right),
              auto.right.cashoutEnabled ? auto.right.cashoutMultiplier : undefined,
              options
            )
          : undefined
      ])
      for (const [zone, res] of [
        ['left', left],
        ['right', right]
      ] as const) {
        if (res?.betId) {
          myBetIdRef.current[zone] = res.betId
          changed = true
        } else if (res?.aborted) {
          anyAborted = true
        }
      }
      setHardSentBetIds([left?.betId, right?.betId].filter((id): id is string => !!id))
    } else {
      if (resolved.left > 0) {
        const res = await submitBetLeft(
          String(resolved.left),
          auto.left.cashoutEnabled ? auto.left.cashoutMultiplier : undefined
        )
        if (res?.betId) {
          myBetIdRef.current.left = res.betId
          changed = true
        } else if (res?.aborted) {
          anyAborted = true
        }
      }
      if (resolved.right > 0) {
        const res = await submitBetRight(
          String(resolved.right),
          auto.right.cashoutEnabled ? auto.right.cashoutMultiplier : undefined
        )
        if (res?.betId) {
          myBetIdRef.current.right = res.betId
          changed = true
        } else if (res?.aborted) {
          anyAborted = true
        }
      }
    }

    // v12.4 ask 7 (G8): a transport abort (no channel / never joined /
    // throw) is no longer a silent console line — the stake stays queued
    // (the post-submit clear below only runs for zones that got a bet id)
    // and the player is told it holds for the next round. Datadog logging
    // happens at the abort site in useCrashBetSubmit.
    if (anyAborted) fireNotice('submit-aborted')

    // Post-submit persistence (v12 R12 formula): zones without auto-rebet
    // clear WHAT WAS SUBMITTED after a successful submit — max(0, prev −
    // resolved) — so a chip placed during the POST gap survives as the
    // queued remainder instead of being silently wiped. Auto zones keep
    // their full stake: that IS the next-round re-bet.
    setStakes((prev) => {
      const next = { ...prev }
      if (myBetIdRef.current.left && !zoneAutoRef.current.left.autoEnabled) {
        next.left = Math.max(0, prev.left - resolved.left)
      }
      if (myBetIdRef.current.right && !zoneAutoRef.current.right.autoEnabled) {
        next.right = Math.max(0, prev.right - resolved.right)
      }
      return next
    })

    // v12.1 minor (b): purge the undo history AGAIN after the post-submit
    // clear. Any entry pushed during the POST gap snapshotted the
    // PRE-subtraction stakes — they still include the submitted amount, so
    // popping one would resurrect it as an invisible queued stake that
    // auto-submits next round. Undo never crosses the submission boundary;
    // chips placed from here on undo normally.
    undoHistoryRef.current = []
    setUndoDepth(0)

    // Drop the locked snapshot for any zone whose submit failed (no id) —
    // its card must not keep showing a stake that never became a bet.
    setSubmittedStakes({
      left: myBetIdRef.current.left ? resolved.left : 0,
      right: myBetIdRef.current.right ? resolved.right : 0
    })
    setSubmittedCashout((prev) => ({
      left: myBetIdRef.current.left ? prev.left : undefined,
      right: myBetIdRef.current.right ? prev.right : undefined
    }))

    if (changed) forceBetIds((n) => n + 1)
  }, [player.balanceLoaded, submitBetLeft, submitBetRight, currency, fireNotice])

  // The hard lock (see `useBetLock` above): send both zones now, once. A board
  // that never saw this round open sends nothing (as at race_starting below),
  // and a countdown held back by a frozen tab holds the stakes for the next
  // round. Either way the round is over for this board: the guards latch.
  onHardLockRef.current = (sent: BetLockSent) => {
    if (!observedBettingOpenRef.current) return
    if (sent.frozen) {
      observedBettingOpenRef.current = false
      fireNotice('submit-aborted')
      return
    }
    hardSendRef.current = { joinDeadline: stageTwoAt(sent.at, sent.lockLeadS).joinDeadline }
    void triggerSubmission()
  }

  // Schedule the submission at race_starting, fall back to race_started.
  //
  // TRANSITION-GATED: this effect also re-runs when triggerSubmission's
  // identity churns mid-round (any game/player object change recreates the
  // submit callbacks). Phase actions must fire only on ENTRY into a phase —
  // otherwise chips queued during a live round could be submitted INTO that
  // round on a re-run (front-running class). The client must never emit a bet
  // for a running round.
  const prevPhaseRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const prevPhase = prevPhaseRef.current
    if (phase === prevPhase) return
    prevPhaseRef.current = phase

    // Leaving race_starting with the early-submit timer still pending: drop it
    // (entry into race_started submits directly; any other exit means the
    // round is gone).
    if (prevPhase === 'race_starting' && submitTimerRef.current) {
      clearTimeout(submitTimerRef.current)
      submitTimerRef.current = null
    }

    if (phase === 'race_ready') {
      observedBettingOpenRef.current = true
      return
    }
    if (phase === 'race_starting') {
      if (!observedBettingOpenRef.current) return
      // A hard round has no 1.5 s timer: it sends at its lock.
      if (betLock.hard) return
      submitTimerRef.current = setTimeout(() => {
        submitTimerRef.current = null
        void triggerSubmission()
      }, SUBMIT_DELAY_MS)
      return
    }
    if (phase === 'race_started') {
      // No race_started fallback on a hard round: a board that did not lock
      // sends nothing, and its stakes carry to the next round.
      if (observedBettingOpenRef.current && !betLock.hard) void triggerSubmission()
      // Betting for this round is definitively over — latch both guards so no
      // later code path (effect churn, late chip placement) can submit into
      // the live round. Stakes placed from here on are next-round only; the
      // race_ready round-reset re-opens the guards for the next round.
      observedBettingOpenRef.current = false
      submissionStartedRef.current = true
    }
  }, [phase, triggerSubmission, betLock.hard])

  // Unmount safety: never leave the early-submit timer armed.
  useEffect(
    () => () => {
      if (submitTimerRef.current) clearTimeout(submitTimerRef.current)
    },
    []
  )

  // Round reset on race_ready: reset the submission guard + per-zone bet ids.
  const roundId = game.races[0]?.uuid
  const prevRoundIdRef = useRef(roundId)
  useEffect(() => {
    if (phase !== 'race_ready') return
    const isNewRound = prevRoundIdRef.current !== roundId
    prevRoundIdRef.current = roundId
    if (!isNewRound && submissionStartedRef.current === false) return
    submissionStartedRef.current = false
    myBetIdRef.current = { left: undefined, right: undefined }
    setHardSentBetIds((prev) => (prev.length === 0 ? prev : []))
    // New round → nothing is locked/submitted yet.
    setSubmittedStakes(emptyStakes())
    setSubmittedCashout({ left: undefined, right: undefined })
    // Reset undo history so Undo can't restore the previous round's stakes and
    // the history ref doesn't grow unbounded across rounds (FIX 6).
    undoHistoryRef.current = []
    setUndoDepth(0)
    // Clear any lingering cash-out in-flight flags for the new round.
    setCashingOut({ left: false, right: false })
    // A settling overlay must never bleed into the next round — and neither
    // may last round's deferred "just missed" notice.
    setOptCashout({ left: null, right: null })
    pendingJustMissedRef.current = false
    if (justMissedTimerRef.current) {
      clearTimeout(justMissedTimerRef.current)
      justMissedTimerRef.current = null
    }
    // v12 (R20): the tap shield dies with the round — a fresh betting
    // window is immediately buildable.
    cashShieldRef.current = { left: undefined, right: undefined }
    // Balance-pin bookkeeping is per-round — drop last round's bet ids so
    // the sets can't grow unbounded across a session.
    manualCashoutBetIdsRef.current.clear()
    balanceSyncedBetIdsRef.current.clear()
    // The vanished-bet watcher's per-round memory dies with the round too.
    seenBetIdsRef.current.clear()
    forceBetIds((n) => n + 1)
  }, [phase, roundId])

  // ── Refresh persistence (ASK 2 — v15) ──────────────────────────────────
  // On a page load with an active bet in the current race, rehydrate the
  // per-zone bet binding (myBetIdRef) + the submitted-stake snapshot from the
  // store bets — mirroring ChipPlacementPanel's currentBets → placedChips
  // restore. Without this the whole display goes dark on refresh: myBetIdRef
  // (per-mount ref) + submittedStakes (useState) reset to empty on mount and
  // were only ever written by a live submit, so a still-riding bet vanished.
  //
  // Crash bet ids are DETERMINISTIC (generateBetId), so the refetched server
  // bet carries the SAME id the client minted — the resolver rebuilds the
  // binding purely from currentBets, keyed by nonce ('slot1' = right zone).
  // ONLY restores a zone whose binding is still empty, so it can never
  // clobber an in-progress live submit; the race_ready round reset clears the
  // restored binding exactly like a live one (no re-submit — the restored bet
  // is not a queued stake). What is UNRECOVERABLE: the armed auto-cashout
  // target pill (BetDTO has no autoCashoutMultiplier); auto-cashout still
  // fires server-side, and the locked pill simply degrades to the seat until
  // settlement (a cashed bet's realized multiplier survives via
  // cashoutMultiplier).
  useEffect(() => {
    if (currentBets.length === 0) return
    // Nothing to restore while a submission is being built/fired this round —
    // the live path owns the binding.
    const restored = resolveRehydratedZoneBets(currentBets)
    let didRestore = false
    for (const zone of ['left', 'right'] as Zone[]) {
      const rz = restored[zone]
      if (rz === undefined) continue
      if (myBetIdRef.current[zone] !== undefined) continue // never clobber a live binding
      myBetIdRef.current[zone] = rz.id
      setSubmittedStakes((prev) => (prev[zone] === rz.stake ? prev : { ...prev, [zone]: rz.stake }))
      didRestore = true
    }
    if (didRestore) {
      // This round's bet already went out (pre-refresh) — latch the single-shot
      // guard so the scheduling pipeline treats the round as submitted and the
      // race_ready reset re-opens it for the next round (mirrors
      // ChipPlacementPanel's betsActuallySent). Guarded so it never re-arms a
      // fresh betting window (only latches while a bet is genuinely riding).
      submissionStartedRef.current = true
      forceBetIds((n) => n + 1)
    }
  }, [currentBets])

  // ---------------------------------------------------------------------------
  // Active bet lookup + cash-out
  // ---------------------------------------------------------------------------
  const myBet: Record<Zone, Bet | undefined> = useMemo(() => {
    const findBet = (id: string | undefined) =>
      id ? currentBets.find((b) => b.id === id) : undefined
    return { left: findBet(myBetIdRef.current.left), right: findBet(myBetIdRef.current.right) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBets, undoDepth, myBetIdRef.current.left, myBetIdRef.current.right])
  myBetRef.current = myBet

  // G10: whether each zone's bet is still awaiting server acceptance. Read
  // non-reactively — every acceptance also replaces the bet in bets.byId,
  // which re-renders us through currentBets, so this render-time read stays
  // fresh without a second subscription.
  const unacceptedBets = useBetStore.getState().unacceptedBets
  const myBetUnaccepted: Record<Zone, boolean> = {
    left: myBet.left !== undefined && unacceptedBets[myBet.left.id] !== undefined,
    right: myBet.right !== undefined && unacceptedBets[myBet.right.id] !== undefined
  }

  // ── Vanished-bet watcher (v12.4 ask 7 — G6/G10) ────────────────────────
  // A tracked bet leaving the store before the round reset means it was
  // REJECTED (handleBetRejected removes it from bets.byId) or purged as
  // never-accepted at settle (settleBetsForRace). Without this, the zone
  // sat in a breathing 'placing' card until race_ready with no explanation.
  // Clear the zone's submission snapshot (the card returns to the builder)
  // and surface the failure. `seenBetIds` gates on the bet having actually
  // EXISTED in the store, so a submit whose optimistic insert never landed
  // can't false-positive; the set is cleared by the race_ready round reset.
  const seenBetIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const zone of ['left', 'right'] as Zone[]) {
      const id = myBetIdRef.current[zone]
      if (!id) continue
      if (myBet[zone] !== undefined) {
        seenBetIdsRef.current.add(id)
        continue
      }
      if (!seenBetIdsRef.current.has(id)) continue
      // The bet existed and is now GONE — rejected or purged.
      seenBetIdsRef.current.delete(id)
      myBetIdRef.current[zone] = undefined
      setSubmittedStakes((prev) => (prev[zone] === 0 ? prev : { ...prev, [zone]: 0 }))
      setSubmittedCashout((prev) =>
        prev[zone] === undefined ? prev : { ...prev, [zone]: undefined }
      )
      forceBetIds((n) => n + 1)
      // The global bet:rejected handler surfaces the MAPPED reason through
      // the ui-store toast (rendered by the panel's notice bridge, G7) —
      // the generic treatment only fires when no reason toast is in flight.
      if (!useUIStore.getState().toast.show) fireNotice('bet-failed')
    }
  }, [myBet, fireNotice])

  // v12 tap shield arming (R15): stamp the moment a zone's bet transitions
  // INTO a cashed status — the instant the card converts from cash-out
  // button to builder. Transition-guarded so re-renders can't re-arm it.
  const prevZoneStatusRef = useRef<Record<Zone, string | undefined>>({
    left: undefined,
    right: undefined
  })
  useEffect(() => {
    for (const zone of ['left', 'right'] as Zone[]) {
      const status = myBet[zone]?.status
      const prev = prevZoneStatusRef.current[zone]
      prevZoneStatusRef.current[zone] = status
      if (status === prev) continue
      if (status !== undefined && CASHED_STATUSES.includes(status)) {
        cashShieldRef.current[zone] = Date.now()
      }
    }
  }, [myBet])

  // ── v11 BALANCE PIN — auto-cash-out wallet re-sync ──────────────────────
  // Manual cash-out applies the REST response's balance directly (below).
  // AUTO cash-out is enforced entirely server-side (AutoCashoutScheduler →
  // CashoutBetUseCase): the only client-side balance trigger is the realtime
  // `bet:cashed-out` push on the player channel — best-effort (fire-and-
  // forget in Go, relayed through Elixir). If it drops, NOTHING refreshes
  // the balance: settlement deliberately skips cashed_out bets and never
  // calls updateBalance. So when OUR bet transitions to cashed_out WITHOUT
  // a local REST cashout having run, fetch the balance here — once per bet
  // id (no fetch storms), skipped for bets the manual path handled (its
  // response already carried the authoritative balance).
  // NOTE (2026-07-06): the auto-cash-out "balance never updates" bug was NOT
  // a dropped push (Datadog showed 100% delivery) — it was demo mode's
  // updateBalance re-applying a render-stale demo balance, clobbering the
  // credit (fixed in usePlayerBalance). This watcher stays as the safety
  // net for genuine real-money push drops.
  const manualCashoutBetIdsRef = useRef<Set<string>>(new Set())
  const balanceSyncedBetIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const zone of ['left', 'right'] as Zone[]) {
      const bet = myBet[zone]
      if (!bet || bet.status !== 'cashed_out') continue
      if (manualCashoutBetIdsRef.current.has(bet.id)) continue
      if (balanceSyncedBetIdsRef.current.has(bet.id)) continue
      balanceSyncedBetIdsRef.current.add(bet.id)
      void updateBalance()
    }
  }, [myBet, updateBalance])

  // Fire the deferred "just missed" once the crash reaches this viewer's
  // frames (or via the fallback timer when it never does).
  useEffect(() => {
    if (visualCrashStatus !== 'crashed') return
    if (!pendingJustMissedRef.current) return
    pendingJustMissedRef.current = false
    if (justMissedTimerRef.current) {
      clearTimeout(justMissedTimerRef.current)
      justMissedTimerRef.current = null
    }
    fireNotice('cashout-just-missed')
  }, [visualCrashStatus, fireNotice])

  // Unmount safety for the fallback timer.
  useEffect(
    () => () => {
      if (justMissedTimerRef.current) clearTimeout(justMissedTimerRef.current)
    },
    []
  )

  // Overlay hygiene 1: a CONFIRMED terminal outcome clears a held overlay.
  //   won/cashed_out — the cashout landed via another path (auto-cashout, a
  //     lost-response push): the derived banner + chime take over.
  //   cancelled — the round voided; the stake came back.
  // 'lost' deliberately does NOT clear it: handleCrashOccurred flips pending
  // bets to lost the moment the crash EVENT lands — often while our POST is
  // still in flight — and a pre-crash cashout the server already paid will
  // overwrite that to cashed_out when the response arrives. The response
  // handler (or the timeout below) resolves the lost case.
  useEffect(() => {
    for (const zone of ['left', 'right'] as Zone[]) {
      const opt = optCashout[zone]
      if (!opt) continue
      const status = myBet[zone]?.status
      if (status !== undefined && (CASHED_STATUSES.includes(status) || status === 'cancelled')) {
        setOptCashout((prev) =>
          prev[zone]?.betId === opt.betId ? { ...prev, [zone]: null } : prev
        )
      }
    }
  }, [myBet, optCashout])

  // Overlay hygiene 2: a hung POST (no response at all) degrades the
  // settling card back to the live card after a generous timeout — betId-
  // guarded so a newer overlay is never clobbered by an old timer. ONE
  // effect PER ZONE, each keyed on ITS overlay only: a single effect keyed
  // on the whole record would restart this zone's countdown every time the
  // OTHER zone's overlay mounts/clears (a hung card could outlive the
  // timeout ~2x with both zones in play).
  const leftOpt = optCashout.left
  useEffect(() => {
    if (!leftOpt) return undefined
    const timer = setTimeout(() => {
      setOptCashout((prev) => (prev.left?.betId === leftOpt.betId ? { ...prev, left: null } : prev))
    }, OPTIMISTIC_CASHOUT_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [leftOpt])
  const rightOpt = optCashout.right
  useEffect(() => {
    if (!rightOpt) return undefined
    const timer = setTimeout(() => {
      setOptCashout((prev) =>
        prev.right?.betId === rightOpt.betId ? { ...prev, right: null } : prev
      )
    }, OPTIMISTIC_CASHOUT_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [rightOpt])

  // Queue the "Just missed" acknowledgment so it lands IN SYNC with the
  // on-screen bust — the anti-tell twin of the visual busted transition.
  // Shared by the rejected-POST 'after-crash' branch and the crash-hold
  // dead-tap guards below (v47): while the visual status is 'armed' the
  // notice is deferred to the crashed-edge effect (with the fallback
  // timer); once the frames have shown the crash (or there is no visual
  // truth left — 'undriven') it fires immediately.
  const armDeferredJustMissed = useCallback(() => {
    if (visualCrashStatusRef.current === 'armed') {
      pendingJustMissedRef.current = true
      if (justMissedTimerRef.current) clearTimeout(justMissedTimerRef.current)
      justMissedTimerRef.current = setTimeout(() => {
        justMissedTimerRef.current = null
        // NEVER announce a crash the viewer's frames haven't shown: if the
        // visual status is STILL 'armed' (glass latency beyond this
        // fallback — the fresh-beacon hold tolerates up to t_crash+8s),
        // stay silent and leave the pending flag for the crashed-edge
        // effect. A dropped notice beats a spoiler. 'undriven' (driver
        // unmounted) fires: there is no visual truth left to wait for.
        if (pendingJustMissedRef.current && visualCrashStatusRef.current !== 'armed') {
          pendingJustMissedRef.current = false
          fireNotice('cashout-just-missed')
        }
      }, JUST_MISSED_FALLBACK_MS)
    } else {
      fireNotice('cashout-just-missed')
    }
  }, [fireNotice])

  const cashout = useCallback(
    async (zone: Zone, shownMultiplier?: number) => {
      const bet = zone === 'left' ? myBet.left : myBet.right
      if (!bet) return
      if (!hasStartedClimbing) {
        // Crash-hold dead tap (v47): the round crashed on the event clock
        // but this viewer's frames haven't shown it yet — the card still
        // reads live and answers the tap with the click sound. The old
        // silent return here was the "button robbed me" ticket: click,
        // then Busted 1.8s later with zero acknowledgment. Queue the same
        // deferred "Just missed" the rejected-POST path gets.
        if (hasCrashed) armDeferredJustMissed()
        return
      }
      if (RESOLVED_STATUSES.includes(bet.status)) {
        // Same window, later tick: the bet already flipped 'lost' on the
        // event clock while the card still shows the live climb.
        if (bet.status === 'lost') armDeferredJustMissed()
        return
      }
      // In-flight guard (FIX 4): a rapid second tap must not fire a second POST
      // against a still-pending bet.
      if (cashingOutRef.current[zone]) return

      // Round identity at the tap: a response that outlives its round (hung
      // transport across the race_ready reset) must never replay its
      // snapshot into the NEXT round's wallet/bets.
      const tapRoundId = prevRoundIdRef.current

      // Snapshot the number ON SCREEN (display clock, passed by the zone;
      // event clock fallback). The server pays on its own leading clock, so
      // the confirmed figure reconciles UP from this — the settling card can
      // promise it without ever having to walk a number back.
      const mTap = Math.max(1, shownMultiplier ?? liveMultiplier)
      const overlay: OptimisticCashout = {
        betId: bet.id,
        multiplier: mTap,
        payout: bet.betAmount * mTap
      }
      const clearOverlay = () =>
        setOptCashout((prev) => (prev[zone]?.betId === bet.id ? { ...prev, [zone]: null } : prev))

      setCashingOut((prev) => ({ ...prev, [zone]: true }))
      // The INSTANT feedback: the provisional settling card. Store bet stays
      // PENDING — no chime, no banner, no balance move until confirm.
      setOptCashout((prev) => ({ ...prev, [zone]: overlay }))
      try {
        const result = await cashoutBet(bet.id, bet.raceId, game.id)
        if (result.success && result.data) {
          if (prevRoundIdRef.current !== tapRoundId) {
            // STALE-ROUND success: the server settled this cashout in its
            // own round, but the response arrived after the race_ready
            // reset. Its balance snapshot predates the next round's stake
            // deductions and its bet belongs to a dead round — applying
            // either would show phantom funds / mutate a settled bet.
            // Reconcile authoritatively instead.
            clearOverlay()
            await updateBalance()
            return
          }
          // Mark BEFORE the store update below re-renders us — the balance
          // pin's effect must never see this bet as an auto-cash-out.
          manualCashoutBetIdsRef.current.add(bet.id)
          betActions.betCashedOut(bet.id, result.data.cashoutMultiplier, result.data.actualPayout)

          if (isDemoMode) {
            const payoutAmount = fromInteger(result.data.actualPayout, bet.currency)
            // Apply-time read of the demo balance (usePlayerBalance's
            // documented pattern): the render-captured player.balance is
            // TAP-time stale — two zones cashed inside one round-trip
            // would erase each other's credit, and balanceChanged's
            // unconditional demo-store mirror would make the loss stick.
            playerActions.balanceChanged({
              amount: useDemoStore.getState().balance.amount + payoutAmount,
              currency: bet.currency
            })
          } else if (result.data.balance) {
            playerActions.balanceChanged({
              amount: fromInteger(result.data.balance.amount, result.data.balance.currency),
              currency: result.data.balance.currency
            })
          } else {
            await updateBalance()
          }
          // The store now carries the CONFIRMED (≥) figures — the derived
          // banner + chime take over from the settling card.
          clearOverlay()
        } else {
          // Rollback, per reason. The overlay drops in EVERY branch — no
          // celebratory state was ever shown, so no yank-back is possible;
          // the card returns to whatever the store's truth renders.
          console.error('Cashout failed:', result.error)
          clearOverlay()
          const kind =
            result.errorKind ?? (result.error === 'Session expired' ? 'session' : 'unknown')
          switch (kind) {
            case 'after-crash':
              // The crash beat the tap inside the network slop. Soft notice
              // only — and DEFERRED until this viewer's frames show the
              // crash (the visual busted transition then lands in sync).
              armDeferredJustMissed()
              break
            case 'not-pending':
            case 'round-inactive':
              // The bet already resolved via another path — which WAY is
              // unknown here (BetNotPendingForCashout covers cashed AND
              // lost/voided). Still no notice, no assumption: the store's
              // terminal status renders the honest outcome (banner or
              // cinematic bust) — but only once the store HAS that status.
              // `bet:cashed-out` is a single at-most-once push, so a socket
              // reconnect can swallow the auto-cash-out confirmation and
              // leave the store bet PENDING: the card then keeps climbing and
              // busts at crash on a bet the server already paid. Server says
              // resolved + store says pending = pull the truth.
              //
              // Fire-and-forget on BOTH: `promisifyPush` wires no timeout, so
              // awaiting here could pin `cashingOut` (set false in `finally`)
              // for the rest of the round.
              void fetchBets()
              void updateBalance()
              break
            case 'session':
              fireNotice('session-expired')
              break
            default:
              // before-climb / network / not-found / unknown: the bet is
              // still live and re-tappable — the generic retry line (G11).
              fireNotice('cashout-failed')
          }
        }
      } finally {
        setCashingOut((prev) => ({ ...prev, [zone]: false }))
      }
    },
    [
      myBet,
      hasStartedClimbing,
      liveMultiplier,
      hasCrashed,
      armDeferredJustMissed,
      cashoutBet,
      game.id,
      betActions,
      playerActions,
      isDemoMode,
      updateBalance,
      fetchBets,
      fireNotice
    ]
  )

  return {
    chips,
    selectedChipIdx,
    selectChip,
    stakes,
    placeChip,
    double,
    undo,
    clear,
    clearZone,
    repeat,
    canUndo,
    canControl,
    hasRepeatable,
    zoneAuto,
    setZoneAuto,
    myBet,
    cashout,
    cashingOut,
    optCashout,
    liveMultiplier,
    isClimbing,
    hasStartedClimbing,
    isBettingOpen,
    stageTwo,
    hasCrashed,
    crashedMultiplier,
    totalStaked,
    balance,
    currency,
    inputPrecision,
    minBet,
    maxBet,
    submittedStakes,
    submittedCashout,
    notice,
    fireNotice,
    myBetUnaccepted
  }
}
